import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaPaperPlane, FaEdit, FaSave, FaTimes, FaClock, FaTag } from 'react-icons/fa';
import useSupport from '../hooks/useSupport';
import { useAuth } from '../contexts/AuthContext';
import '../styles/SupportDetail.css';

const SupportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    currentTicket,
    agents,
    loading,
    error,
    fetchTicket,
    updateTicket,
    addMessage
  } = useSupport();

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    status: '',
    priority: '',
    assigned_to: '',
    category: ''
  });
  const [newMessage, setNewMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  // Charger le ticket au montage
  useEffect(() => {
    if (id) {
      fetchTicket(id);
    }
  }, [id, fetchTicket]);

  // Initialiser le formulaire d'édition
  useEffect(() => {
    if (currentTicket) {
      setEditForm({
        status: currentTicket.status || '',
        priority: currentTicket.priority || '',
        assigned_to: currentTicket.assigned_to || '',
        category: currentTicket.category || ''
      });
    }
  }, [currentTicket]);

  // Scroll vers le bas des messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentTicket?.messages]);

  // Sauvegarder les modifications
  const handleSave = async () => {
    try {
      await updateTicket(id, editForm);
      setIsEditing(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  // Annuler l'édition
  const handleCancel = () => {
    if (currentTicket) {
      setEditForm({
        status: currentTicket.status || '',
        priority: currentTicket.priority || '',
        assigned_to: currentTicket.assigned_to || '',
        category: currentTicket.category || ''
      });
    }
    setIsEditing(false);
  };

  // Envoyer un message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sendingMessage) return;

    try {
      setSendingMessage(true);
      await addMessage(id, {
        message: newMessage.trim(),
        sender_id: user.id,
        is_internal: isInternal
      });
      setNewMessage('');
      setIsInternal(false);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR');
  };

  // Obtenir la couleur du statut
  const getStatusColor = (status) => {
    const colors = {
      'open': '#e74c3c',
      'in_progress': '#f39c12',
      'resolved': '#27ae60',
      'closed': '#95a5a6'
    };
    return colors[status] || '#95a5a6';
  };

  // Obtenir la couleur de la priorité
  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': '#e74c3c',
      'high': '#f39c12',
      'normal': '#3498db',
      'low': '#95a5a6'
    };
    return colors[priority] || '#95a5a6';
  };

  // Obtenir le nom de l'utilisateur
  const getUserName = (ticket) => {
    if (ticket.business_name) {
      return ticket.business_name;
    }
    if (ticket.client_first_name && ticket.client_last_name) {
      return `${ticket.client_first_name} ${ticket.client_last_name}`;
    }
    if (ticket.prestataire_first_name && ticket.prestataire_last_name) {
      return `${ticket.prestataire_first_name} ${ticket.prestataire_last_name}`;
    }
    return ticket.user_email;
  };

  // Obtenir le nom de l'expéditeur d'un message
  const getMessageSender = (message) => {
    if (message.sender_business_name) {
      return message.sender_business_name;
    }
    if (message.sender_client_first_name && message.sender_client_last_name) {
      return `${message.sender_client_first_name} ${message.sender_client_last_name}`;
    }
    if (message.sender_prestataire_first_name && message.sender_prestataire_last_name) {
      return `${message.sender_prestataire_first_name} ${message.sender_prestataire_last_name}`;
    }
    return message.sender_email || 'Utilisateur inconnu';
  };

  if (loading) {
    return (
      <div className="support-detail-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Chargement du ticket...</p>
        </div>
      </div>
    );
  }

  if (error || !currentTicket) {
    return (
      <div className="support-detail-page">
        <div className="error-container">
          <p>{error || 'Ticket non trouvé'}</p>
          <button onClick={() => navigate('/support')}>Retour à la liste</button>
        </div>
      </div>
    );
  }

  return (
    <div className="support-detail-page">
      {/* Header */}
      <div className="ticket-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/support')}>
            <FaArrowLeft />
            Retour
          </button>
          <div className="ticket-info">
            <h1>{currentTicket.subject}</h1>
            <div className="ticket-meta">
              <span className="ticket-id">#{currentTicket.id.substring(0, 8)}</span>
              <span className="ticket-user">
                <FaUser /> {getUserName(currentTicket)}
              </span>
              <span className="ticket-email">{currentTicket.user_email}</span>
            </div>
          </div>
        </div>
        <div className="header-right">
          {!isEditing ? (
            <button className="edit-btn" onClick={() => setIsEditing(true)}>
              <FaEdit />
              Modifier
            </button>
          ) : (
            <div className="edit-actions">
              <button className="save-btn" onClick={handleSave}>
                <FaSave />
                Sauvegarder
              </button>
              <button className="cancel-btn" onClick={handleCancel}>
                <FaTimes />
                Annuler
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="ticket-content">
        {/* Informations du ticket */}
        <div className="ticket-sidebar">
          <div className="ticket-details">
            <h3>Détails du ticket</h3>
            
            <div className="detail-group">
              <label>Statut</label>
              {isEditing ? (
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="open">Ouvert</option>
                  <option value="in_progress">En cours</option>
                  <option value="resolved">Résolu</option>
                  <option value="closed">Fermé</option>
                </select>
              ) : (
                <span
                  className="badge status"
                  style={{ backgroundColor: getStatusColor(currentTicket.status) }}
                >
                  {currentTicket.status.replace('_', ' ')}
                </span>
              )}
            </div>

            <div className="detail-group">
              <label>Priorité</label>
              {isEditing ? (
                <select
                  value={editForm.priority}
                  onChange={(e) => setEditForm(prev => ({ ...prev, priority: e.target.value }))}
                >
                  <option value="low">Faible</option>
                  <option value="normal">Normale</option>
                  <option value="high">Élevée</option>
                  <option value="urgent">Urgent</option>
                </select>
              ) : (
                <span
                  className="badge priority"
                  style={{ backgroundColor: getPriorityColor(currentTicket.priority) }}
                >
                  {currentTicket.priority}
                </span>
              )}
            </div>

            <div className="detail-group">
              <label>Catégorie</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editForm.category}
                  onChange={(e) => setEditForm(prev => ({ ...prev, category: e.target.value }))}
                />
              ) : (
                <span className="category-value">
                  <FaTag /> {currentTicket.category}
                </span>
              )}
            </div>

            <div className="detail-group">
              <label>Assigné à</label>
              {isEditing ? (
                <select
                  value={editForm.assigned_to}
                  onChange={(e) => setEditForm(prev => ({ ...prev, assigned_to: e.target.value }))}
                >
                  <option value="">Non assigné</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.email}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="assigned-value">
                  <FaUser />
                  {currentTicket.assigned_email || 'Non assigné'}
                </span>
              )}
            </div>

            <div className="detail-group">
              <label>Type d'utilisateur</label>
              <span className="user-type">
                {currentTicket.user_type}
              </span>
            </div>

            <div className="detail-group">
              <label>Créé le</label>
              <span className="date-value">
                <FaClock /> {formatDate(currentTicket.created_at)}
              </span>
            </div>

            <div className="detail-group">
              <label>Mis à jour le</label>
              <span className="date-value">
                <FaClock /> {formatDate(currentTicket.updated_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Conversation */}
        <div className="conversation-section">
          <div className="conversation-header">
            <h3>Conversation</h3>
            <span className="message-count">
              {currentTicket.messages?.length || 0} message(s)
            </span>
          </div>

          <div className="messages-container">
            {/* Message initial */}
            <div className="message initial-message">
              <div className="message-header">
                <div className="sender-info">
                  <FaUser />
                  <span className="sender-name">{getUserName(currentTicket)}</span>
                  <span className="sender-type">({currentTicket.user_type})</span>
                </div>
                <span className="message-date">
                  {formatDate(currentTicket.created_at)}
                </span>
              </div>
              <div className="message-content">
                <h4>Demande initiale</h4>
                <p>{currentTicket.description}</p>
              </div>
            </div>

            {/* Messages de conversation */}
            {currentTicket.messages?.map((message) => (
              <div
                key={message.id}
                className={`message ${message.is_internal ? 'internal' : 'public'} ${
                  message.sender_type === 'admin' ? 'admin' : 'user'
                }`}
              >
                <div className="message-header">
                  <div className="sender-info">
                    <FaUser />
                    <span className="sender-name">{getMessageSender(message)}</span>
                    <span className="sender-type">({message.sender_type})</span>
                    {message.is_internal && (
                      <span className="internal-badge">Interne</span>
                    )}
                  </div>
                  <span className="message-date">
                    {formatDate(message.created_at)}
                  </span>
                </div>
                <div className="message-content">
                  <p>{message.message}</p>
                </div>
              </div>
            ))}

            <div ref={messagesEndRef} />
          </div>

          {/* Formulaire de nouveau message */}
          <form className="message-form" onSubmit={handleSendMessage}>
            <div className="form-header">
              <h4>Nouveau message</h4>
              <label className="internal-checkbox">
                <input
                  type="checkbox"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                />
                Message interne (non visible par le client)
              </label>
            </div>
            <div className="form-body">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Tapez votre message ici..."
                rows={4}
                disabled={sendingMessage}
              />
              <button type="submit" disabled={!newMessage.trim() || sendingMessage}>
                <FaPaperPlane />
                {sendingMessage ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SupportDetail;